import { useEffect, useState } from 'react';
import './AuthForm.css';
import {
  fetchCompanyDataByBIK,
  fetchCompanyDataByINN,
  registrationOrganisation,
} from '../../api/AuthApi';
import OrgSignupForm from '../OrgSignupForm/OrgSignupForm';
import UserSignupForm from '../UserSignupForm/UserSignupForm';
import UserLoginForm from '../UserLoginForm/UserLoginForm';
import LogoutPopup from '../LogoutPopup/LogoutPopup';
import formatPhoneNumber from '../../utils/formatPhoneNumber';
import { useAuth } from '../../Context/AuthContext';
import { normalizeNumberForDataBase } from '../../utils/phone';
import UserNavRedirect from '../UserNavRedirect/UserNavRedirect';

export default function AuthForm({
  activeBlok,
  toggleMenu,
  handleSelectedBlock,
  fetchOrganisation,
  handleClearData,
  isMobile,
}) {
  const [formData, setFormData] = useState({ errors: {} });
  const [activeForm, setActiveForm] = useState('');
  const [logoutError, setLogoutError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, logout, register } = useAuth();

  useEffect(() => {
    setActiveForm(activeBlok);
    setFormData({ errors: {} });
  }, [activeBlok]);

  const toggleForm = (nameForm) => {
    setActiveForm(nameForm);
    handleSelectedBlock(nameForm);
  };

  const requiredFields = {
    login: ['login', 'password'],
    register: ['email', 'password', 'confirmPassword', 'phone', 'login'],
    registerOrg: ['name', 'address', 'phone', 'inn', 'ogrn'],
  };
  const validateField = (name, value) => {
    let errorMessage = '';

    // Проверка на обязательность
    if (requiredFields[activeForm]?.includes(name) && !value?.trim()) {
      return 'Поле обязательно';
    }

    const digitsOnly = value.replace(/\D/g, '');

    switch (name) {
      case 'phone':
        if (digitsOnly.length < 11) {
          errorMessage = 'Номер должен содержать 11 цифр';
        }
        break;

      case 'inn':
        if (!/^\d{10}$|^\d{12}$/.test(digitsOnly)) {
          errorMessage = 'ИНН должен содержать 10 или 12 цифр';
        }
        break;

      case 'bik':
        if (digitsOnly.length !== 0 && digitsOnly.length !== 9) {
          errorMessage = 'БИК должен содержать ровно 9 цифр';
        }
        break;

      default:
        break;
    }

    return errorMessage;
  };

  // Централизованное форматирование значений
  const formatFieldValue = (name, value, prevValue = '') => {
    switch (name) {
      case 'phone':
        return formatPhoneNumber(value, prevValue);

      case 'bik':
        // Убираем все символы, кроме цифр, и обрезаем до 9 символов
        return value.replace(/\D/g, '').slice(0, 9);

      case 'inn':
        // Только цифры, максимум 12 символов
        return value.replace(/\D/g, '').slice(0, 12);

      default:
        return value;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const formattedValue = formatFieldValue(name, value, formData[name] || '');

    const errorMessage = validateField(name, formattedValue);

    setFormData((prev) => {
      const updatedErrors = { ...prev.errors };

      if (errorMessage) {
        updatedErrors[name] = errorMessage;
      } else {
        delete updatedErrors[name];
      }

      return {
        ...prev,
        [name]: formattedValue,
        errors: updatedErrors,
      };
    });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const errorMessage = validateField(name, value);

    setFormData((prev) => {
      const updatedErrors = { ...prev.errors };

      if (errorMessage) {
        updatedErrors[name] = errorMessage;
      } else {
        delete updatedErrors[name];
      }

      return {
        ...prev,
        errors: updatedErrors,
      };
    });

    if (name === 'phone' && value.replace(/\D/g, '').length < 11) {
      setFormData((prev) => ({
        ...prev,
        phone: '',
      }));
    }
  };
  const handleFocus = (e) => {
    const { name, value } = e.target;

    // Если поле phone пустое, добавляем +7 при фокусе
    if (name === 'phone' && !value) {
      const formattedValue = ''; // Начальное значение

      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue, // Устанавливаем +7
        errors: {
          ...prev.errors,
          [name]: '', // Очищаем ошибку для phone
        },
      }));
    }
  };
  const handleChangeErrors = (err, field) => {
    const messageError = err.data.message;

    let message;
    if (field === 'bik') {
      if (messageError === 'The BIK is incorrect') {
        message = 'БИК не найден';
      } else if (messageError === 'One or more parameters are missing') {
        message = 'Заполните БИК правильно';
      } else {
        message = '';
      }
    } else if (field === 'inn') {
      if (messageError === 'The INN is incorrect') {
        message = 'ИНН не найден';
      } else if (messageError === 'Duplicate entry') {
        message = 'Организация с таким ИНН уже зарегистрирована';
      } else if (messageError === 'One or more parameters are missing') {
        message = 'Заполните ИНН правильно';
      } else {
        message = '';
      }
    }

    setFormData((prev) => ({
      ...prev,
      errors: {
        ...prev.errors,
        [field]: message,
      },
    }));
  };
  // Регистрация организации
  const handleSubmit = async (e) => {
    e.preventDefault();

    const activeRequiredFields = requiredFields[activeForm] || [];
    let errors = {};

    // Валидация полей перед отправкой
    activeRequiredFields.forEach((field) => {
      const value = formData[field] || '';

      const errorMessage = validateField(field, value);

      if (errorMessage) {
        errors[field] = errorMessage;
      }
    });

    // Объединяем с текущими ошибками
    const mergedErrors = {
      ...formData.errors,
      ...errors,
    };

    // Проверка: есть ли хоть одна ошибка?
    const hasErrors = Object.values(mergedErrors).some(
      (error) => error && error.trim() !== ''
    );

    if (hasErrors) {
      setFormData((prev) => ({
        ...prev,
        errors: mergedErrors,
      }));
      setIsLoading(false);
      return; // Прерываем отправку!
    }

    // Если все ок - продолжаем отправку
    setIsLoading('registerOrg');

    try {
      // Создаем объект FormData с заполненными полями
      const FormData = {};
      Object.keys(formData).forEach((key) => {
        if (formData[key] && key !== 'errors') {
          // Исключаем пустые поля и объект errors

          FormData[key] =
            key === 'phone'
              ? normalizeNumberForDataBase(formData[key])
              : formData[key];
        }
      });

      // Отправляем на сервер только заполненные поля
      await registrationOrganisation(FormData);
      setIsLoading(false);
      toggleForm(null);
      fetchOrganisation();
    } catch (err) {
      console.error(err.data.message);
      handleChangeErrors(err, 'bik');
      handleChangeErrors(err, 'inn');
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFillByINN = async (e, inn) => {
    e.preventDefault();

    setIsLoading('inn');
    fetchCompanyDataByINN(inn)
      .then((res) => {
        setFormData((prev) => {
          // Создаем объект для обновления данных
          const updatedData = { ...prev };
          console.log(updatedData);
          // Обновляем только те поля, значения которых не являются ложными
          Object.keys(res.data).forEach((key) => {
            if (res.data[key]) {
              // Проверяем, что значение не ложное
              updatedData[key] = res.data[key];
            }
          });

          // Список полей, ошибки которых нужно удалить при наличии валидных данных
          const fieldsToCheck = requiredFields[activeBlok];

          // Создаем обновленный объект ошибок
          const updatedErrors = { ...prev.errors };

          fieldsToCheck.forEach((field) => {
            if (updatedData[field]) {
              updatedErrors[field] = ''; // Очищаем ошибку, если поле заполнено
            }
          });

          return {
            ...updatedData,
            errors: updatedErrors, // Обновляем ошибки
          };
        });
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        handleChangeErrors(err, 'inn');
      });
  };

  const handleFillByBIK = async (e, bik) => {
    e.preventDefault();

    setIsLoading('bik');
    fetchCompanyDataByBIK(bik)
      .then((res) => {
        setFormData((prev) => {
          // Создаем объект для обновления данных
          const updatedData = { ...prev };

          // Обновляем только те поля, значения которых не являются ложными
          Object.keys(res.data).forEach((key) => {
            if (res.data[key]) {
              // Проверяем, что значение не ложное
              updatedData[key] = res.data[key];
            }
          });

          return updatedData;
        });
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        handleChangeErrors(err, 'bik');
      });
  };

  // Регистрация пользователя
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    const { email, password, confirmPassword, phone, login } = formData;
    let errors = {};
    const normPhone = normalizeNumberForDataBase(phone);
    // Валидация email
    if (!email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Введите корректный e-mail';
    }

    // Валидация телефона
    if (!phone?.trim() || phone.replace(/\D/g, '').length !== 11) {
      errors.phone = 'Введите корректный номер телефона';
    }

    // Валидация пароля и подтверждения пароля
    if (!password?.trim() || password !== confirmPassword) {
      errors.password = 'Пароли не совпадают';
      errors.confirmPassword = 'Пароли не совпадают';
    }

    // Если есть ошибки, показываем их и прерываем выполнение
    if (Object.keys(errors).length > 0) {
      setFormData((prev) => ({ ...prev, errors }));
      return;
    }

    try {
      setFormData((prev) => ({ ...prev, errors: {} }));
      // Если валидация прошла успешно, выполняем регистрацию
      await register({ email, password, phone: normPhone, login }); // Используем метод из контекста
      handleLoginSubmit(e); // Войти автоматически после регистрации
      setFormData({ errors: {} }); // Очищаем ошибки после успешной регистрации
    } catch (error) {
      console.log(error);
      // Обработка ошибок (например, если логин уже занят)
      if (error.data.message === 'Duplicate entry') {
        setFormData((prev) => ({
          ...prev,
          errors: { ...prev.errors, login: 'Этот логин занят' },
        }));
      }
    }
  };

  // sign-in в аккаунт
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setIsLoading('sign-in'); // Устанавливаем состояние загрузки

    const { login: userLogin, password } = formData;
    let errors = {};

    if (!userLogin?.trim()) {
      errors.login = 'Поле логин не может быть пустым';
    }
    if (!password?.trim()) {
      errors.password = 'Поле пароль не может быть пустым';
    }

    if (Object.keys(errors).length > 0) {
      setFormData((prev) => ({ ...prev, errors }));
      setIsLoading(false);
      return;
    }

    try {
      await login({ login: userLogin, password });
      if (isMobile) {
        // handleSelectedBlock('redirect'); // Вызывается только при успешном входе
      } else {
        handleSelectedBlock(null)
        toggleMenu('menu-1');
      }
      setFormData({ errors: {} });
    } catch (error) {
      const errorMessage =
        error.data?.message === 'User not found'
          ? 'Неверный логин или пароль'
          : 'Ошибка входа';
      setFormData((prev) => ({
        ...prev,
        errors: { login: errorMessage, password: errorMessage },
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Завершение всех сессий (logout)
  const handleLogout = async () => {
    try {
      await logout(); // Используем метод logout из контекста, который уже очищает
      handleSelectedBlock(null);
      setLogoutError('');
      handleClearData();
    } catch (error) {
      setActiveForm(null);
      console.error('Ошибка выхода:', error); // Логирование ошибки
      setLogoutError('Ошибка выхода, попробуйте снова');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='auth-forms'>
      <div className='auth-forms__container'>
        <UserSignupForm
          activeForm={activeForm}
          formData={formData}
          handleChange={handleChange}
          handleSubmit={handleRegisterSubmit}
          toggleForm={toggleForm}
          handleBlur={handleBlur}
          handleFocus={handleFocus}
        />
        <UserLoginForm
          activeForm={activeForm}
          formData={formData}
          handleChange={handleChange}
          handleSubmit={handleLoginSubmit}
          toggleForm={toggleForm}
        />
        <OrgSignupForm
          handleFillByINN={handleFillByINN}
          handleFillByBIK={handleFillByBIK}
          activeForm={activeForm}
          formData={formData}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          toggleForm={handleSelectedBlock}
          requiredFields={requiredFields.registerOrg}
          handleBlur={handleBlur}
          handleFocus={handleFocus}
          isLoading={isLoading}
        />
        <LogoutPopup
          activeForm={activeForm}
          logoutError={logoutError}
          onCancel={() => handleSelectedBlock(null)}
          onLogout={handleLogout}
        />
        <UserNavRedirect activeForm={activeForm} onCancel={() => handleSelectedBlock(null)} />
      </div>
    </div>
  );
}
